import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dataService } from '../services/dataService';
import { locationService } from '../services/locationService';
import BottomTabNav from '../shared/components/BottomTabNav';
import Footer from '../shared/components/Footer';
import './CustomerEditProfile.css';

const CustomerEditProfile = () => {
  const { user, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    preferences: {
      notifications: true,
      emailUpdates: true,
      smsUpdates: false,
      promotions: true
    }
  });
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // Load countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countriesData = await locationService.getCountries();
        setCountries(countriesData);
      } catch (error) {
        console.error('Error loading countries:', error);
      }
    };
    loadCountries();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    const loadCities = async () => {
      if (!formData.country) {
        setCities([]);
        return;
      }
      try {
        setLoadingCities(true);
        const citiesData = await locationService.getCitiesByCountry(formData.country);
        setCities(citiesData);
      } catch (error) {
        console.error('Error loading cities:', error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };
    loadCities();
  }, [formData.country]);

  useEffect(() => {
    // Load user data when component mounts
    const loadUserData = async () => {
    if (user) {
        let customerRecord = null;
        if (user.email) {
          try {
            customerRecord = await dataService.getCustomerByEmail(user.email);
          } catch (error) {
            console.error('Error loading customer record:', error);
          }
        }

      setFormData(prev => ({
        ...prev,
          name: customerRecord?.name || user.user_metadata?.name || '',
        email: user.email || '',
          phone: customerRecord?.phone || user.user_metadata?.phone || '',
          dateOfBirth: customerRecord?.date_of_birth || user.user_metadata?.date_of_birth || '',
          address: customerRecord?.address || user.user_metadata?.address || '',
          city: customerRecord?.city || user.user_metadata?.city || '',
          country: customerRecord?.country || user.user_metadata?.country || '',
          postalCode: customerRecord?.postal_code || user.user_metadata?.postal_code || '',
        preferences: {
          notifications: user.user_metadata?.preferences?.notifications ?? true,
          emailUpdates: user.user_metadata?.preferences?.emailUpdates ?? true,
          smsUpdates: user.user_metadata?.preferences?.smsUpdates ?? false,
          promotions: user.user_metadata?.preferences?.promotions ?? true
        }
      }));
    }
    };
    loadUserData();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('preferences.')) {
      const prefKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [prefKey]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      // If country changes, clear city
      if (name === 'country') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          city: '' // Clear city when country changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSaveStatus('');

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        setSaveStatus('error');
        setIsLoading(false);
        return;
      }

      // Update user profile
      const updateData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        date_of_birth: formData.dateOfBirth,
        address: formData.address.trim(),
        city: formData.city.trim(),
        country: formData.country.trim(),
        postal_code: formData.postalCode.trim(),
        preferences: formData.preferences
      };

      await updateUserProfile(updateData);
      setSaveStatus('success');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSaveStatus('');
      }, 3000);

    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="edit-profile-container">
        <div className="edit-profile-content">
          {/* Header */}
          <div className="edit-profile-header">
            <Link to="/profile" className="back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <h1 className="edit-profile-title">Edit Profile</h1>
            <div className="header-spacer"></div>
          </div>

          {/* Profile Avatar Section */}
          <div className="profile-avatar-section">
            <div className="avatar-container">
              <div className="current-avatar">
                <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="50" fill="url(#editAvatarGradient)"/>
                  <text 
                    x="50" 
                    y="58" 
                    textAnchor="middle" 
                    fontSize="32" 
                    fontWeight="700" 
                    fill="white"
                    fontFamily="system-ui, -apple-system, sans-serif"
                  >
                    {formData.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </text>
                  <defs>
                    <linearGradient id="editAvatarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#667eea"/>
                      <stop offset="100%" stopColor="#764ba2"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <button className="change-avatar-btn" type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L12 22M2 12L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <p className="avatar-hint">Tap to change your profile picture</p>
          </div>

          {/* Edit Form */}
          <form className="edit-profile-form" onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-section">
              <h2 className="section-title">Basic Information</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    disabled
                    placeholder="Email cannot be changed"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="form-section">
              <h2 className="section-title">Address Information</h2>
              
              <div className="form-group">
                <label htmlFor="address">Street Address</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter your street address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Select your country"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.code || country.name} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  {formData.country ? (
                    <select
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={loadingCities}
                      placeholder="Select your city"
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  ) : (
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                      placeholder="Select country first"
                      disabled
                  />
                  )}
                </div>
                </div>
                
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="postalCode">Postal Code</label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder="Enter postal code"
                  />
                </div>
              </div>
            </div>

            {/* Preferences */}
            <div className="form-section">
              <h2 className="section-title">Preferences</h2>
              
              <div className="preferences-grid">
                <div className="preference-item">
                  <div className="preference-content">
                    <h3>Push Notifications</h3>
                    <p>Receive notifications about order updates</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="preferences.notifications"
                      checked={formData.preferences.notifications}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="preference-item">
                  <div className="preference-content">
                    <h3>Email Updates</h3>
                    <p>Get order confirmations and updates via email</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="preferences.emailUpdates"
                      checked={formData.preferences.emailUpdates}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="preference-item">
                  <div className="preference-content">
                    <h3>SMS Updates</h3>
                    <p>Receive SMS notifications for important updates</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="preferences.smsUpdates"
                      checked={formData.preferences.smsUpdates}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="preference-item">
                  <div className="preference-content">
                    <h3>Promotional Offers</h3>
                    <p>Receive special offers and promotional content</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="preferences.promotions"
                      checked={formData.preferences.promotions}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Status Messages */}
            {saveStatus === 'success' && (
              <div className="status-message success">
                <div className="status-icon">✅</div>
                <p>Profile updated successfully!</p>
              </div>
            )}

            {saveStatus === 'error' && (
              <div className="status-message error">
                <div className="status-icon">❌</div>
                <p>Error updating profile. Please check all fields and try again.</p>
              </div>
            )}

            {/* Submit Button */}
            <button 
              type="submit" 
              className="save-profile-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      </div>
      
      <Footer websiteType="customer" />
      <BottomTabNav />
    </div>
  );
};

export default CustomerEditProfile;
